import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Mail, MousePointerClick, Eye, TrendingUp, TrendingDown, Users,
  AlertTriangle, CheckCircle, XCircle, BarChart3, PieChart,
  RefreshCw, Loader2, Calendar, Activity, ArrowUp, ArrowDown,
  UserX, Send, Clock
} from "lucide-react";

const ENGAGEMENT_COLORS = {
  high: { bg: "bg-green-100", text: "text-green-700", border: "border-green-200" },
  medium: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" },
  low: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200" }
};

export default function EmailAnalyticsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [periodDays, setPeriodDays] = useState(30);
  
  // Summary data
  const [summary, setSummary] = useState({
    total_sent: 0,
    total_delivered: 0,
    total_opened: 0,
    total_clicked: 0,
    total_bounced: 0,
    delivery_rate: 0,
    open_rate: 0,
    click_rate: 0,
    bounce_rate: 0
  });
  
  // Trends data
  const [trends, setTrends] = useState([]);
  
  // Patient engagement
  const [engagements, setEngagements] = useState([]);
  const [lowEngagement, setLowEngagement] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, trendsRes, engagementRes, lowEngRes] = await Promise.all([
        api.get(`/ehr/email-analytics/summary?days=${periodDays}`),
        api.get(`/ehr/email-analytics/trends?days=${periodDays}`),
        api.get("/ehr/email-analytics/patient-engagement?limit=20"),
        api.get("/ehr/email-analytics/low-engagement?threshold=30")
      ]);
      
      setSummary(summaryRes.data.summary || {});
      setTrends(trendsRes.data.trends || []);
      setEngagements(engagementRes.data.engagements || []);
      setLowEngagement(lowEngRes.data.patients || []);
      
    } catch (error) {
      console.error("Error loading analytics:", error);
      toast.error(t("emailAnalytics.loadError", "Failed to load analytics"));
    } finally {
      setLoading(false);
    }
  }, [periodDays, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getEngagementBadge = (level) => {
    const colors = ENGAGEMENT_COLORS[level] || ENGAGEMENT_COLORS.low;
    return (
      <Badge className={`${colors.bg} ${colors.text}`}>
        {level === "high" && <TrendingUp className="w-3 h-3 mr-1" />}
        {level === "medium" && <Activity className="w-3 h-3 mr-1" />}
        {level === "low" && <TrendingDown className="w-3 h-3 mr-1" />}
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="email-analytics-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-8 h-8" />
                <h1 className="text-3xl font-bold">{t("emailAnalytics.title", "Email Analytics")}</h1>
              </div>
              <p className="text-blue-200">
                {t("emailAnalytics.subtitle", "Track email engagement and identify communication issues")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={periodDays.toString()} onValueChange={(v) => setPeriodDays(parseInt(v))}>
                <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">{t("emailAnalytics.period.7days", "Last 7 days")}</SelectItem>
                  <SelectItem value="14">{t("emailAnalytics.period.14days", "Last 14 days")}</SelectItem>
                  <SelectItem value="30">{t("emailAnalytics.period.30days", "Last 30 days")}</SelectItem>
                  <SelectItem value="90">{t("emailAnalytics.period.90days", "Last 90 days")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="secondary" onClick={loadData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                {t("common.refresh", "Refresh")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="border-l-4 border-l-blue-500" data-testid="stat-sent">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{t("emailAnalytics.stats.sent", "Emails Sent")}</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.total_sent}</p>
                </div>
                <Send className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500" data-testid="stat-delivered">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{t("emailAnalytics.stats.deliveryRate", "Delivery Rate")}</p>
                  <p className="text-2xl font-bold text-green-600">{summary.delivery_rate}%</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500" data-testid="stat-opened">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{t("emailAnalytics.stats.openRate", "Open Rate")}</p>
                  <p className="text-2xl font-bold text-purple-600">{summary.open_rate}%</p>
                </div>
                <Eye className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500" data-testid="stat-clicked">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{t("emailAnalytics.stats.clickRate", "Click Rate")}</p>
                  <p className="text-2xl font-bold text-amber-600">{summary.click_rate}%</p>
                </div>
                <MousePointerClick className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500" data-testid="stat-bounced">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{t("emailAnalytics.stats.bounceRate", "Bounce Rate")}</p>
                  <p className="text-2xl font-bold text-red-600">{summary.bounce_rate}%</p>
                </div>
                <XCircle className="w-8 h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Low Engagement Alert */}
        {lowEngagement.length > 0 && (
          <Card className="mb-8 border-red-200 bg-red-50" data-testid="low-engagement-alert">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium text-red-800">
                    {t("emailAnalytics.lowEngagementAlert", "Low Engagement Detected")}
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    {lowEngagement.length} {t("emailAnalytics.patientsLowEngagement", "patients have low email engagement. Consider phone calls or alternative communication.")}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {lowEngagement.slice(0, 5).map((patient) => (
                      <Badge key={patient.patient_id} variant="outline" className="bg-white border-red-200">
                        <UserX className="w-3 h-3 mr-1" />
                        {patient.patient_name}
                      </Badge>
                    ))}
                    {lowEngagement.length > 5 && (
                      <Badge variant="outline" className="bg-white border-red-200">
                        +{lowEngagement.length - 5} {t("common.more", "more")}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4" />
              {t("emailAnalytics.tabs.overview", "Overview")}
            </TabsTrigger>
            <TabsTrigger value="engagement" className="flex items-center gap-2" data-testid="tab-engagement">
              <Users className="w-4 h-4" />
              {t("emailAnalytics.tabs.engagement", "Patient Engagement")}
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Funnel Visualization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  {t("emailAnalytics.funnel.title", "Email Funnel")}
                </CardTitle>
                <CardDescription>
                  {t("emailAnalytics.funnel.description", "Track emails from send to click")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Sent */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4 text-blue-600" />
                        {t("emailAnalytics.funnel.sent", "Sent")}
                      </span>
                      <span className="font-medium">{summary.total_sent}</span>
                    </div>
                    <Progress value={100} className="h-3 [&>div]:bg-blue-500" />
                  </div>
                  
                  {/* Delivered */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        {t("emailAnalytics.funnel.delivered", "Delivered")}
                      </span>
                      <span className="font-medium">{summary.total_delivered} ({summary.delivery_rate}%)</span>
                    </div>
                    <Progress value={summary.delivery_rate} className="h-3 [&>div]:bg-green-500" />
                  </div>
                  
                  {/* Opened */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-purple-600" />
                        {t("emailAnalytics.funnel.opened", "Opened")}
                      </span>
                      <span className="font-medium">{summary.total_opened} ({summary.open_rate}%)</span>
                    </div>
                    <Progress value={summary.open_rate} className="h-3 [&>div]:bg-purple-500" />
                  </div>
                  
                  {/* Clicked */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <MousePointerClick className="w-4 h-4 text-amber-600" />
                        {t("emailAnalytics.funnel.clicked", "Clicked")}
                      </span>
                      <span className="font-medium">{summary.total_clicked} ({summary.click_rate}%)</span>
                    </div>
                    <Progress value={summary.click_rate} className="h-3 [&>div]:bg-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Daily Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  {t("emailAnalytics.trends.title", "Daily Trends")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="min-w-[600px]">
                    <div className="grid grid-cols-[100px_repeat(7,1fr)] gap-2 text-xs font-medium text-muted-foreground mb-2">
                      <div>{t("emailAnalytics.trends.date", "Date")}</div>
                      <div className="text-center">{t("emailAnalytics.trends.sent", "Sent")}</div>
                      <div className="text-center">{t("emailAnalytics.trends.delivered", "Delivered")}</div>
                      <div className="text-center">{t("emailAnalytics.trends.opened", "Opened")}</div>
                      <div className="text-center">{t("emailAnalytics.trends.clicked", "Clicked")}</div>
                      <div className="text-center">{t("emailAnalytics.trends.bounced", "Bounced")}</div>
                      <div className="text-center">{t("emailAnalytics.trends.openRate", "Open %")}</div>
                      <div className="text-center">{t("emailAnalytics.trends.clickRate", "Click %")}</div>
                    </div>
                    <ScrollArea className="h-[300px]">
                      {trends.map((day, idx) => (
                        <div key={idx} className="grid grid-cols-[100px_repeat(7,1fr)] gap-2 py-2 border-b text-sm">
                          <div className="font-medium">{day.date}</div>
                          <div className="text-center text-blue-600">{day.sent}</div>
                          <div className="text-center text-green-600">{day.delivered}</div>
                          <div className="text-center text-purple-600">{day.opened}</div>
                          <div className="text-center text-amber-600">{day.clicked}</div>
                          <div className="text-center text-red-600">{day.bounced}</div>
                          <div className="text-center">
                            {day.sent > 0 ? Math.round((day.opened / day.sent) * 100) : 0}%
                          </div>
                          <div className="text-center">
                            {day.opened > 0 ? Math.round((day.clicked / day.opened) * 100) : 0}%
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ENGAGEMENT TAB */}
          <TabsContent value="engagement" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t("emailAnalytics.engagement.title", "Patient Email Engagement")}
                </CardTitle>
                <CardDescription>
                  {t("emailAnalytics.engagement.description", "Ranked by engagement score (opens + clicks)")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {engagements.map((patient, idx) => (
                      <div
                        key={patient.patient_id}
                        className={`p-4 rounded-lg border ${ENGAGEMENT_COLORS[patient.engagement_level]?.border || ""} ${ENGAGEMENT_COLORS[patient.engagement_level]?.bg || "bg-slate-50"}`}
                        data-testid={`patient-engagement-${patient.patient_id}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{patient.patient_name}</p>
                              <p className="text-xs text-muted-foreground">ID: {patient.patient_id}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getEngagementBadge(patient.engagement_level)}
                            <span className="text-lg font-bold">{patient.engagement_score}</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-4 text-center text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">{t("emailAnalytics.engagement.sent", "Sent")}</p>
                            <p className="font-medium text-blue-600">{patient.emails_sent}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">{t("emailAnalytics.engagement.opened", "Opened")}</p>
                            <p className="font-medium text-purple-600">{patient.emails_opened}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">{t("emailAnalytics.engagement.clicked", "Clicked")}</p>
                            <p className="font-medium text-amber-600">{patient.emails_clicked}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">{t("emailAnalytics.engagement.openRate", "Open Rate")}</p>
                            <p className="font-medium">
                              {patient.emails_sent > 0 ? Math.round((patient.emails_opened / patient.emails_sent) * 100) : 0}%
                            </p>
                          </div>
                        </div>
                        
                        {patient.last_email_opened && (
                          <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {t("emailAnalytics.engagement.lastOpened", "Last opened")}: {formatDate(patient.last_email_opened)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
